import { useContext } from "react";
import EstimateContractContext from "../context/EstimateContractContext";

import type {
    LineEdit,
    LineEdits,
    LaborLineEdit,
    LaborLineEdits,
} from "../types";
import { cloneMeasurements } from "../utils/measurementScope";

export default function useEstimateContractContextManager() {
    const context = useContext(EstimateContractContext);
    if (!context) {
        throw new Error(
            "useEstimateContractContextManager must be used within EstimateContractContext.Provider",
        );
    }

    const { values, setValues, referenceValue, initLoading, setInitLoading } =
        context;

    function getValues() {
        return values;
    }

    function getMaterials() {
        return values.materials;
    }

    function getLabor() {
        return values.labor;
    }

    function getMeasurements() {
        return values.measurements;
    }

    function getAttributes() {
        return values.attributes;
    }

    function updateValues(next: any) {
        setValues(next);
    }

    function updateMaterials(materials: LineEdits) {
        setValues((prev: any) => ({ ...prev, materials }));
    }

    function patchMaterialEdit(editKey: string, patch: Partial<LineEdit>) {
        patchMaterialEdits({ [editKey]: patch });
    }

    function patchMaterialEdits(patches: Record<string, Partial<LineEdit>>) {
        setValues((prev: any) => {
            const materials = { ...prev.materials };
            let changed = false;

            for (const [editKey, patch] of Object.entries(patches)) {
                const current = materials[editKey];
                const next: LineEdit = {
                    selectedProductId: current?.selectedProductId ?? null,
                    selectedColor: current?.selectedColor ?? null,
                    quantity: current?.quantity ?? 0,
                    quantityOverridden: current?.quantityOverridden,
                    supplierUnitPrice: current?.supplierUnitPrice ?? null,
                    supplierId: current?.supplierId ?? null,
                    supplierName: current?.supplierName ?? null,
                    ...patch,
                };

                if (
                    current?.selectedProductId !== next.selectedProductId ||
                    (current?.selectedColor ?? null) !== (next.selectedColor ?? null) ||
                    current?.quantity !== next.quantity ||
                    current?.quantityOverridden !== next.quantityOverridden ||
                    current?.supplierUnitPrice !== next.supplierUnitPrice ||
                    (current?.supplierId ?? null) !== (next.supplierId ?? null) ||
                    (current?.supplierName ?? null) !== (next.supplierName ?? null)
                ) {
                    changed = true;
                    materials[editKey] = next;
                }
            }

            return changed ? { ...prev, materials } : prev;
        });
    }

    function updateLabor(labor: LaborLineEdits) {
        setValues((prev: any) => ({ ...prev, labor }));
    }

    function patchLaborEdit(editKey: string, patch: Partial<LaborLineEdit>) {
        patchLaborEdits({ [editKey]: patch });
    }

    function patchLaborEdits(patches: Record<string, Partial<LaborLineEdit>>) {
        setValues((prev: any) => {
            const labor = { ...prev.labor };
            let changed = false;

            for (const [editKey, patch] of Object.entries(patches)) {
                const current = labor[editKey];
                const next: LaborLineEdit = {
                    laborServiceId: current?.laborServiceId ?? "",
                    quantity: current?.quantity ?? 0,
                    quantityOverridden: current?.quantityOverridden,
                    supplierUnitPrice: current?.supplierUnitPrice ?? null,
                    supplierId: current?.supplierId ?? null,
                    supplierName: current?.supplierName ?? null,
                    ...patch,
                };

                if (
                    current?.laborServiceId !== next.laborServiceId ||
                    current?.quantity !== next.quantity ||
                    current?.quantityOverridden !== next.quantityOverridden ||
                    current?.supplierUnitPrice !== next.supplierUnitPrice ||
                    (current?.supplierId ?? null) !== (next.supplierId ?? null) ||
                    (current?.supplierName ?? null) !== (next.supplierName ?? null)
                ) {
                    changed = true;
                    labor[editKey] = next;
                }
            }

            return changed ? { ...prev, labor } : prev;
        });
    }

    function updateAttributes(attributes: any) {
        setValues((prev: any) => ({
            ...prev,
            attributes: { ...(attributes || {}) },
        }));
    }

    function patchAttributes(patch: Record<string, unknown>) {
        setValues((prev: any) => ({
            ...prev,
            attributes: {
                ...(prev.attributes || {}),
                ...patch,
            },
        }));
    }

    function updateMeasurements(measurements: any) {
        setValues((prev: any) => ({
            ...prev,
            measurements: cloneMeasurements(measurements),
        }));
    }

    function setReferenceValue(next: any | ((prev: any) => any)) {
        const nextValue =
            typeof next === "function" ? next(referenceValue) : next;
        Object.assign(referenceValue, nextValue);
    }

    return {
        values,
        referenceValue,
        getValues,
        getMaterials,
        getLabor,
        getMeasurements,
        getAttributes,
        setValues,
        setReferenceValue,
        setInitLoading,
        updateValues,
        updateMaterials,
        patchMaterialEdit,
        patchMaterialEdits,
        patchLaborEdit,
        patchLaborEdits,
        updateLabor,
        updateMeasurements,
        updateAttributes,
        patchAttributes,
        initLoading,
    };
}
